import React from "react";
import { Image, Rate } from "antd";
import { useTranslation } from "react-i18next";

import { getHydrationBreakdown } from "@fitness-app/data-models/entities/HydrationEntry";
import {
  MeasurementType,
  type BodyMeasurement,
  type HydrationMeasurement,
  type Measurement,
} from "@fitness-app/data-models/entities/Measurement";

import { useHydrationEntriesForDay } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeResults/hooks/useMeasurementsForRange";
import { DetailRow } from "~/components/EventCalendar/EventPopover";

interface MeasurementContentProps {
  measurement: Measurement;
}

const formatNumber = (value: number) => Intl.NumberFormat("pl-PL").format(value);

const BigValue = ({ value, unit, caption }: { value: string; unit?: string; caption?: string }) => (
  <div className="flex flex-col gap-0.5">
    <div className="flex items-baseline gap-1">
      <span className="text-2xl font-semibold tabular-nums text-gray-900">{value}</span>
      {unit && <span className="text-sm font-medium text-gray-500">{unit}</span>}
    </div>
    {caption && <span className="text-xs text-gray-500">{caption}</span>}
  </div>
);

// TRE-2413: suma dnia rozbita na napoje dodane w apce (woda = reszta sumy).
const HydrationContent = ({ event }: { event: HydrationMeasurement }) => {
  const { data: entries = [], isError } = useHydrationEntriesForDay(event.traineeId, event.eventDate);

  if (isError) {
    return (
      <div className="flex flex-col gap-3">
        <BigValue value={formatNumber(event.data.portion)} unit="ml" />
        <p className="m-0 text-xs text-amber-700">Nie udało się pobrać rozbicia na napoje.</p>
      </div>
    );
  }

  const breakdown = getHydrationBreakdown(event.data.portion, entries);
  const parts = [
    { label: "Woda", value: breakdown.water },
    { label: "Kawa", value: breakdown.coffee },
    { label: "Herbata", value: breakdown.tea },
    { label: "Inne", value: breakdown.other },
  ].filter((part) => part.value);

  return (
    <div className="flex flex-col gap-3">
      <BigValue value={formatNumber(event.data.portion)} unit="ml" />
      <div className="flex flex-col gap-2">
        {parts.map((part) => (
          <DetailRow key={part.label} label={part.label}>
            {formatNumber(part.value)} ml
          </DetailRow>
        ))}
      </div>
    </div>
  );
};

const ItemContent = ({ event }: { event: Measurement }) => {
  const { t } = useTranslation("trainees");

  if (event.type === MeasurementType.WeightMeasurement) {
    return <BigValue value={formatNumber(event.data.weight)} unit="kg" />;
  }

  if (event.type === MeasurementType.BodyMeasurements) {
    const rows = Object.keys(event.data)
      .map((key) => key as BodyMeasurement)
      .filter((key) => event.data[key]);

    return (
      <div className="flex flex-col gap-2">
        {rows.map((key) => (
          <DetailRow key={key} label={t(`${key}`)}>
            {event.data[key]}
            {key === "bf" ? "%" : " cm"}
          </DetailRow>
        ))}
      </div>
    );
  }

  if (event.type === MeasurementType.FeedbackAboutTraining) {
    return (
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <Rate disabled defaultValue={event.data.rate ?? 0} className="text-base" />
          <span className="text-[13px] font-medium text-gray-700">
            {t(`workouts:clientRatingOptionsNumber.${event.data.rate}`)}
          </span>
        </div>
        {event.data.comment ? (
          <p className="m-0 rounded-lg bg-gray-50 px-3 py-2 text-[13px] leading-5 text-gray-700">
            {event.data.comment}
          </p>
        ) : (
          <p className="m-0 text-[13px] text-gray-400">Bez komentarza</p>
        )}
      </div>
    );
  }

  if (event.type === MeasurementType.HydrationMeasurement) {
    return <HydrationContent event={event} />;
  }

  if (event.type === MeasurementType.HealthStepsMeasurement) {
    return (
      <BigValue
        value={formatNumber(event.data.steps)}
        unit="kroków"
        caption={event.data.provider === "manual" ? t("healthSync.manual.source") : undefined}
      />
    );
  }

  if (event.type === MeasurementType.ClientPhoto) {
    return (
      <Image.PreviewGroup preview={{ className: "ph-no-capture" }}>
        <div className="grid grid-cols-3 gap-2">
          {event.data.content.map((photo) => (
            <div key={photo.photoId} className="flex flex-col gap-1">
              <Image
                className="ph-no-capture aspect-[3/4] rounded-md object-cover"
                width="100%"
                src={photo.thumbnailUrl || photo.url}
                preview={{ src: photo.url }}
              />
              <span className="text-center text-xs text-gray-500">{t(`photos.type.${photo.type}`)}</span>
            </div>
          ))}
        </div>
      </Image.PreviewGroup>
    );
  }

  return null;
};

const MeasurementContent = ({ measurement }: MeasurementContentProps) => <ItemContent event={measurement} />;

export default MeasurementContent;
